/**
 * Client-side PII pattern redaction for the activity log.
 *
 * Defense in depth: the lexruntime ActivityService re-redacts every
 * event server-side ([extensions/lexiun/src/activity/redact.ts]),
 * so a client that forgets to redact still doesn't leak PII into
 * the SQLite store. We redact here too so:
 *   1. dev tools (network tab, Sentry breadcrumbs) don't see the
 *      raw PII even before it leaves the browser.
 *   2. the per-event payload byte budget is spent on actual content
 *      rather than CPF / CNPJ / email noise.
 *
 * Patterns mirror `feedback-sanitize.ts` (CPF/CNPJ/email) plus a
 * JWT body matcher so accidental token captures (e.g. stack-trace
 * URLs) are scrubbed before persistence.
 */

const REDACTED_TOKENS = {
  cpf: "[REDACTED:cpf]",
  cnpj: "[REDACTED:cnpj]",
  email: "[REDACTED:email]",
  jwt: "[REDACTED:jwt]",
} as const;

// CPF: NNN.NNN.NNN-NN — also the unformatted 11-digit form so a
// user typing their own CPF without dots is still covered.
const CPF_RE = /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g;
// CNPJ: NN.NNN.NNN/NNNN-NN
const CNPJ_RE = /\b\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}\b/g;
// Email — RFC-ish but conservative.
const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
// JWT body (header.payload.sig — base64url segments).
const JWT_RE = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;

/** Redact PII from a string. Returns the scrubbed value + a flag. */
export function redactString(s: string): { value: string; matched: boolean } {
  let out = s;
  let matched = false;
  const before1 = out;
  out = out.replace(JWT_RE, REDACTED_TOKENS.jwt);
  if (out !== before1) matched = true;
  const before2 = out;
  out = out.replace(CPF_RE, REDACTED_TOKENS.cpf);
  if (out !== before2) matched = true;
  const before3 = out;
  out = out.replace(CNPJ_RE, REDACTED_TOKENS.cnpj);
  if (out !== before3) matched = true;
  const before4 = out;
  out = out.replace(EMAIL_RE, REDACTED_TOKENS.email);
  if (out !== before4) matched = true;
  return { value: out, matched };
}

/**
 * Recursively redact every string-valued field in a payload. Numbers,
 * booleans, null, Date pass through. Nested objects + arrays are
 * walked. Pure — does not mutate the caller's input.
 */
export function redactPayload(payload: Record<string, unknown>): {
  payload: Record<string, unknown>;
  matched: boolean;
} {
  const result = walk(payload, false);
  return {
    payload: result.value as Record<string, unknown>,
    matched: result.matched,
  };
}

function walk(v: unknown, matched: boolean): { value: unknown; matched: boolean } {
  if (typeof v === "string") {
    const r = redactString(v);
    return { value: r.value, matched: matched || r.matched };
  }
  if (Array.isArray(v)) {
    let acc = matched;
    const out = v.map((item) => {
      const r = walk(item, false);
      acc = acc || r.matched;
      return r.value;
    });
    return { value: out, matched: acc };
  }
  if (v && typeof v === "object" && !(v instanceof Date)) {
    let acc = matched;
    const out: Record<string, unknown> = {};
    for (const [k, sub] of Object.entries(v as Record<string, unknown>)) {
      const r = walk(sub, false);
      acc = acc || r.matched;
      out[k] = r.value;
    }
    return { value: out, matched: acc };
  }
  return { value: v, matched };
}

/** Truncate text to N chars with a "…[truncated]" suffix. */
export function truncateText(s: string, maxChars: number): string {
  if (s.length <= maxChars) return s;
  return `${s.slice(0, maxChars - 12)}…[truncated]`;
}
